import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, BACKEND_URL } from "../lib/api";

export default function CouplesClubPage() {
  const [board, setBoard] = useState([]);
  useEffect(() => { api.get("/club/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); }, []);

  return (
    <div className="bg-[var(--bg)]">
      <Navbar />
      <section className="bg-[var(--bg-warm)] section-pad">
        <div className="container-wide px-6 md:px-12 text-center">
          <Trophy className="w-8 h-8 mx-auto text-[var(--gold)] mb-4" />
          <p className="font-script text-[var(--gold)] text-4xl mb-2">Locked In Couples Club</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl">Most engaged couples</h1>
          <p className="text-[var(--muted)] mt-4 max-w-xl mx-auto">Couples earn points for every memory uploaded, RSVP collected, story written, and referral made. Plus exclusive badges as you go.</p>
        </div>
      </section>
      <section className="section-pad">
        <div className="container-wide px-6 md:px-12">
          {board.length === 0 ? <p className="text-center text-[var(--muted)] py-20">The leaderboard is just getting started.</p> : (
            <div className="space-y-3">
              {board.map((c, i) => (
                <Link to={`/site/${c.slug}`} key={c.slug} data-testid={`board-${i}`} className="flex items-center gap-4 bg-white border border-[var(--line)] rounded-2xl p-4 hover:border-[var(--gold)] transition">
                  <span className="font-serif text-3xl w-12 text-center text-[var(--muted)]">{i + 1}</span>
                  <div className="w-14 h-14 rounded-full bg-[var(--bg-warm)] overflow-hidden shrink-0">
                    {c.cover_image && <img src={c.cover_image.startsWith("http") ? c.cover_image : `${BACKEND_URL}/api/files/${c.cover_image}`} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-script text-2xl text-[var(--gold)] leading-tight">{c.couple_names}</p>
                    {(c.wedding_date || c.city) && <p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">{c.wedding_date ? new Date(c.wedding_date).toLocaleDateString() : ""}{c.city ? ` · ${c.city}` : ""}</p>}
                    {c.badges_meta?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">{c.badges_meta.slice(0, 4).map((b, j) => <span key={j} title={b.name} className="text-xs">{b.icon}</span>)}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-3xl text-[var(--gold)]">{c.score}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Engagement</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

// Dashboard club page (private)
export function MyClubPage() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/club/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  if (!me) return null;

  const allBadges = me.all_badges || {};
  const earned = new Set(me.badges || []);
  const pct = Math.min(100, Math.round((earned.size / Math.max(1, Object.keys(allBadges).length)) * 100));

  return (
    <div data-testid="club-me">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Couples Club</p>
          <h1 className="font-serif font-light text-4xl">Your engagement</h1>
        </div>
        <Link to="/club" className="btn-outline-gold">Public Leaderboard</Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[var(--ink)] text-white rounded-2xl p-6"><p className="font-serif text-5xl text-[var(--gold)]">{me.score}</p><p className="text-[11px] uppercase tracking-[0.16em] text-white/60 mt-2">Engagement score</p></div>
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6"><p className="font-serif text-5xl">#{me.rank}</p><p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-2">of {me.total} couples</p></div>
        <div className="bg-[var(--bg-warm)] rounded-2xl p-6"><p className="font-serif text-5xl">{earned.size}/{Object.keys(allBadges).length}</p><p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-2">Badges earned</p></div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Badge collection</p>
          <p className="text-xs text-[var(--muted)]">{pct}% complete</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(allBadges).map(([id, info]) => {
            const has = earned.has(id);
            return (
              <div key={id} data-testid={`badge-${id}`} className={`p-4 rounded-xl border ${has ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)] opacity-50"}`}>
                <div className="text-3xl mb-2">{info.icon}</div>
                <p className="font-serif text-lg">{info.name}</p>
                <p className="text-xs text-[var(--muted)] mt-1">{info.desc}</p>
                {has && <p className="text-[10px] uppercase tracking-wider text-[var(--gold)] mt-2">Earned ✓</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
